import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack,
  VStack, Input, Select, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel,
  useToast, useDisclosure, Spinner, Tabs, TabList, Tab, TabPanels, TabPanel,
  SimpleGrid, IconButton, Divider, Switch, Textarea, Image, Tooltip, Code,
} from '@chakra-ui/react';
import { FiArrowLeft, FiRefreshCw, FiPlus, FiEdit2, FiTrash2, FiExternalLink,
         FiUser, FiUsers, FiClock, FiSettings, FiCopy } from 'react-icons/fi';
import api from '../api';

const fmtDt = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';
const fmtDur = (inT, outT) => {
  if (!inT || !outT) return null;
  const mins = Math.round((new Date(outT) - new Date(inT)) / 60000);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins/60)}h ${mins%60}m`;
};

const STATUS_COLORS = { scheduled:'gray', confirmed:'blue', in_progress:'orange', completed:'green', cancelled:'red', no_show:'red' };

// ── Premises Config Modal ─────────────────────────────────────────────────────
function PremisesModal({ isOpen, onClose, onSaved, editPremises, companies }) {
  const BLANK = { company_id:'', name:'', address:'', welcome_message:'', enforcement_mode:'open', enforcement_message:'', planned_visit_window_hours:4, feature_photo:true, feature_video:false, feature_notify_contact:true, feature_notify_signout:true, feature_show_reason:true, feature_require_email:false, feature_pre_reg_landing:true, feature_badge_print:false };
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const set = (k,v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isOpen) setForm(editPremises ? { ...BLANK, ...editPremises, company_id: editPremises.company_id || '' } : BLANK);
  }, [isOpen, editPremises]); // eslint-disable-line

  const save = async () => {
    if (!form.company_id || !form.name) { toast({ title: 'Company and name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      if (editPremises) await api.put(`/visitors/premises/${editPremises.id}`, form);
      else await api.post('/visitors/premises', form);
      toast({ title: 'Premises saved', status: 'success', duration: 1500 });
      onSaved(); onClose();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">{editPremises ? 'Edit Premises' : 'Add Premises'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="11px">Company (determines name & logo on kiosk)</FormLabel>
              <Select size="sm" value={form.company_id} onChange={e => set('company_id', e.target.value)}>
                <option value="">Select company...</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormControl>
            <FormControl isRequired><FormLabel fontSize="11px">Premises Name (e.g. "Main Reception")</FormLabel><Input size="sm" value={form.name} onChange={e => set('name', e.target.value)} /></FormControl>
            <FormControl><FormLabel fontSize="11px">Address</FormLabel><Textarea size="sm" rows={2} value={form.address} onChange={e => set('address', e.target.value)} /></FormControl>
            <FormControl><FormLabel fontSize="11px">Welcome Message (shown on kiosk)</FormLabel><Input size="sm" value={form.welcome_message} onChange={e => set('welcome_message', e.target.value)} placeholder="Welcome to our studios..." /></FormControl>
            <Divider />
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="gray.500">Enforcement</Text>
            <HStack>
              <Box>
                <Text fontSize="11px" fontWeight="600">Mode</Text>
                <Select size="sm" value={form.enforcement_mode} onChange={e => set('enforcement_mode', e.target.value)} w="180px">
                  <option value="open">Open — anyone may sign in</option>
                  <option value="strict">Strict — planned visits only</option>
                </Select>
              </Box>
              <FormControl maxW="100px"><FormLabel fontSize="11px">Match window (hrs)</FormLabel><Input size="sm" type="number" value={form.planned_visit_window_hours} onChange={e => set('planned_visit_window_hours', parseInt(e.target.value))} /></FormControl>
            </HStack>
            {form.enforcement_mode === 'strict' && (
              <FormControl><FormLabel fontSize="11px">Blocked message</FormLabel><Input size="sm" value={form.enforcement_message} onChange={e => set('enforcement_message', e.target.value)} placeholder="You do not have an authorised visit today..." /></FormControl>
            )}
            <Divider />
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="gray.500">Kiosk Features</Text>
            {[
              ['feature_photo', 'Take photo at sign-in'],
              ['feature_video', 'Video Sign-in (15s clip)'],
              ['feature_notify_contact', 'Notify host on arrival (email + photo)'],
              ['feature_notify_signout', 'Notify host on departure'],
              ['feature_show_reason', 'Show visit reason field'],
              ['feature_require_email', 'Require email address'],
              ['feature_pre_reg_landing', 'Show expected visitors on landing screen'],
              ['feature_badge_print', 'Show printable visitor pass on sign-in'],
            ].map(([key, label]) => (
              <HStack key={key} justify="space-between">
                <Text fontSize="12px">{label}</Text>
                <Switch size="sm" isChecked={!!form[key]} onChange={e => set(key, e.target.checked)} colorScheme="orange" />
              </HStack>
            ))}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="orange" onClick={save} isLoading={saving}>Save Premises</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Planned Visit Form ────────────────────────────────────────────────────────
function PlannedVisitModal({ isOpen, onClose, onSaved, premisesList, allUsers, editVisit, prefillVisit }) {
  const BLANK = { premises_id:'', project_id:'', task_id:'', title:'', visit_reason:'', expected_at:'', expected_duration_minutes:'', company_id:'', visitor_contact_ids:[], lead_host_user_id:'', create_calendar_entry:false, create_pre_registration:true };
  const [form, setForm] = useState(BLANK);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const set = (k,v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isOpen) {
      // editVisit takes priority, then prefillVisit, then BLANK
      if (editVisit) setForm({ ...BLANK, ...editVisit });
      else if (prefillVisit) setForm({ ...BLANK, ...prefillVisit });
      else setForm(BLANK);
      api.get('/projects?limit=200').then(r => setProjects(r.data || [])).catch(() => {});
    }
  }, [isOpen, editVisit, prefillVisit]); // eslint-disable-line

  useEffect(() => {
    if (form.project_id) {
      api.get(`/tasks?project_id=${form.project_id}`).then(r => setTasks(r.data || [])).catch(() => {});
    } else setTasks([]);
  }, [form.project_id]);

  const save = async () => {
    if (!form.title || !form.expected_at || !form.lead_host_user_id || !form.project_id) {
      toast({ title: 'Title, date, host, and project required', status: 'warning', duration: 2000 }); return;
    }
    setSaving(true);
    try {
      if (editVisit) await api.put(`/visitors/authorised/${editVisit.id}`, form);
      else await api.post('/visitors/authorised', form);
      toast({ title: 'Planned visit saved', status: 'success', duration: 1500 });
      onSaved(); onClose();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">{editVisit ? 'Edit Planned Visit' : 'New Planned Visit'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl isRequired><FormLabel fontSize="11px">Title</FormLabel><Input size="sm" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Client site survey — Yoruba Gospel" /></FormControl>
            <HStack>
              <FormControl isRequired flex={2}><FormLabel fontSize="11px">Expected Date & Time</FormLabel><Input size="sm" type="datetime-local" value={form.expected_at} onChange={e => set('expected_at', e.target.value)} /></FormControl>
              <FormControl flex={1}><FormLabel fontSize="11px">Duration</FormLabel>
                <Select size="sm" value={form.expected_duration_minutes} onChange={e => set('expected_duration_minutes', e.target.value)}>
                  <option value="">—</option>
                  <option value="30">30 min</option><option value="60">1 hour</option>
                  <option value="120">2 hours</option><option value="240">4 hours</option>
                  <option value="480">Half day</option><option value="960">Full day</option>
                </Select>
              </FormControl>
            </HStack>
            <FormControl><FormLabel fontSize="11px">Premises</FormLabel>
              <Select size="sm" value={form.premises_id} onChange={e => set('premises_id', e.target.value)}>
                <option value="">Select premises...</option>
                {premisesList.map(p => <option key={p.id} value={p.id}>{p.name} ({p.company_name})</option>)}
              </Select>
            </FormControl>
            <HStack>
              <FormControl isRequired flex={2}><FormLabel fontSize="11px">Project</FormLabel>
                <Select size="sm" value={form.project_id} onChange={e => set('project_id', e.target.value)}>
                  <option value="">Select project...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                </Select>
              </FormControl>
              <FormControl flex={2}><FormLabel fontSize="11px">Task (optional)</FormLabel>
                <Select size="sm" value={form.task_id} onChange={e => set('task_id', e.target.value)}>
                  <option value="">— No task —</option>
                  {tasks.map(t => <option key={t.id} value={t.id}>{t.task_title}</option>)}
                </Select>
              </FormControl>
            </HStack>
            <FormControl isRequired><FormLabel fontSize="11px">Lead Host (receives notifications)</FormLabel>
              <Select size="sm" value={form.lead_host_user_id} onChange={e => set('lead_host_user_id', e.target.value)}>
                <option value="">Select host...</option>
                {allUsers.map(u => <option key={u.id} value={u.id}>{u.display_name || u.username}</option>)}
              </Select>
            </FormControl>
            <FormControl><FormLabel fontSize="11px">Visit Reason / Agenda</FormLabel><Textarea size="sm" rows={3} value={form.visit_reason} onChange={e => set('visit_reason', e.target.value)} /></FormControl>
            <Divider />
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="gray.400">Automation</Text>
            <HStack><Switch size="sm" isChecked={!!form.create_calendar_entry} onChange={e => set('create_calendar_entry', e.target.checked)} colorScheme="orange" /><Text fontSize="12px">Create calendar entry on linked task</Text></HStack>
            <HStack><Switch size="sm" isChecked={!!form.create_pre_registration} onChange={e => set('create_pre_registration', e.target.checked)} colorScheme="orange" /><Text fontSize="12px">Pre-register visitors at kiosk</Text></HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="orange" onClick={save} isLoading={saving}>Save Planned Visit</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function SiteVisitorsDashboard({ onBack }) {
  const [liveVisitors, setLiveVisitors] = useState([]);
  const [todayVisitors, setTodayVisitors] = useState([]);
  const [historyVisitors, setHistoryVisitors] = useState([]);
  const [plannedVisits, setPlannedVisits] = useState([]);
  const [premises, setPremises] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabIdx, setTabIdx] = useState(0);
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [editPremises, setEditPremises] = useState(null);
  const [editVisit, setEditVisit] = useState(null);
  const premisesModal = useDisclosure();
  const plannedModal = useDisclosure();
  const toast = useToast();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [liveR, todayR, premR, usersR, compR, avR] = await Promise.all([
        api.get('/visitors/live'),
        api.get('/visitors/today'),
        api.get('/visitors/premises/list'),
        api.get('/users'),
        api.get('/companies?limit=200'),
        api.get('/visitors/authorised/list'),
      ]);
      setLiveVisitors(liveR.data || []);
      setTodayVisitors(todayR.data || []);
      setPremises(premR.data || []);
      setAllUsers(usersR.data || []);
      setCompanies(compR.data || []);
      setPlannedVisits(avR.data || []);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchHistory = async () => {
    try {
      let url = '/visitors?';
      if (historyFrom) url += `from=${historyFrom}&`;
      if (historyTo) url += `to=${historyTo}&`;
      if (historySearch) url += `search=${encodeURIComponent(historySearch)}&`;
      const { data } = await api.get(url);
      setHistoryVisitors(data);
    } catch(e) {}
  };

  useEffect(() => { if (tabIdx === 2) fetchHistory(); }, [tabIdx]); // eslint-disable-line

  const adminSignOut = async (visitorId) => {
    await api.put(`/${visitorId}/sign-out`, {});
    fetchAll();
    toast({ title: 'Visitor signed out', status: 'success', duration: 1500 });
  };

  const deleteAV = async (id) => {
    await api.delete(`/visitors/authorised/${id}`);
    fetchAll();
  };

  const copyKioskUrl = (p) => {
    const url = `${window.location.origin}/visitor/default/${p.slug}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Kiosk URL copied', status: 'success', duration: 1500 });
  };

  const liveCount = liveVisitors.length;

  return (
    <Box p={4} maxW="1400px" mx="auto">
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <Button size="sm" variant="ghost" leftIcon={<FiArrowLeft />} onClick={onBack}>Back</Button>
          <FiUsers size={18} />
          <Text fontWeight="700" fontSize="16px">Site Visitors</Text>
          {liveCount > 0 && <Badge colorScheme="green" fontSize="10px">{liveCount} on site now</Badge>}
        </HStack>
        <Button size="sm" leftIcon={<FiRefreshCw />} variant="ghost" onClick={fetchAll}>Refresh</Button>
      </Flex>

      {loading ? <Flex justify="center" py={10}><Spinner size="lg" /></Flex> : (
        <Tabs index={tabIdx} onChange={setTabIdx}>
          <TabList>
            <Tab fontSize="12px"><HStack><FiUser size={11} /><Text>Live {liveCount > 0 && `(${liveCount})`}</Text></HStack></Tab>
            <Tab fontSize="12px">Today ({todayVisitors.length})</Tab>
            <Tab fontSize="12px">History</Tab>
            <Tab fontSize="12px">Planned Visits ({plannedVisits.length})</Tab>
            <Tab fontSize="12px"><FiSettings size={11} /> Premises</Tab>
          </TabList>
          <TabPanels>

            {/* LIVE */}
            <TabPanel px={0}>
              {liveVisitors.length === 0 ? (
                <Box textAlign="center" py={12} color="gray.400"><FiUsers size={40} style={{ margin: '0 auto 12px' }} /><Text>No visitors currently on site</Text></Box>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {liveVisitors.map(v => (
                    <Box key={v.id} p={4} border="1px" borderColor="green.200" borderRadius="lg" bg="green.50">
                      <Flex justify="space-between" align="flex-start">
                        <HStack align="flex-start" spacing={3}>
                          {v.photo_url ? <Image src={`/api/files/${v.photo_url}`} boxSize="48px" borderRadius="full" objectFit="cover" /> : <Box w="48px" h="48px" borderRadius="full" bg="gray.200" display="flex" alignItems="center" justifyContent="center"><FiUser size={20} /></Box>}
                          <Box>
                            <Text fontWeight="700" fontSize="14px">{v.visitor_name}</Text>
                            {v.visitor_company && <Text fontSize="12px" color="gray.500">{v.visitor_company}</Text>}
                            <Text fontSize="11px" color="gray.400">Visiting: {v.host_name}</Text>
                            <Text fontSize="11px" color="gray.400">{v.premises_name}</Text>
                          </Box>
                        </HStack>
                        <Badge colorScheme="green" fontSize="9px">In</Badge>
                      </Flex>
                      <Flex justify="space-between" align="center" mt={3}>
                        <Text fontSize="11px" color="gray.500">Since {new Date(v.sign_in_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</Text>
                        <Button size="xs" colorScheme="orange" variant="outline" onClick={() => adminSignOut(v.id)}>Sign Out</Button>
                      </Flex>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </TabPanel>

            {/* TODAY */}
            <TabPanel px={0}>
              <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                <Table size="sm">
                  <Thead bg="gray.50"><Tr>
                    <Th fontSize="9px">Visitor</Th><Th fontSize="9px">Company</Th><Th fontSize="9px">Host</Th>
                    <Th fontSize="9px">Premises</Th><Th fontSize="9px">In</Th><Th fontSize="9px">Out</Th>
                    <Th fontSize="9px">Duration</Th><Th />
                  </Tr></Thead>
                  <Tbody>
                    {todayVisitors.length === 0 && <Tr><Td colSpan={8} textAlign="center" py={6} color="gray.400">No visitors today yet</Td></Tr>}
                    {todayVisitors.map(v => (
                      <Tr key={v.id} bg={!v.sign_out_time ? 'green.50' : 'white'}>
                        <Td><Text fontSize="12px" fontWeight="600">{v.visitor_name}</Text></Td>
                        <Td fontSize="11px">{v.visitor_company || '—'}</Td>
                        <Td fontSize="11px">{v.host_name}</Td>
                        <Td fontSize="11px" color="gray.500">{v.premises_name}</Td>
                        <Td fontSize="11px">{new Date(v.sign_in_time).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })}</Td>
                        <Td fontSize="11px">{v.sign_out_time ? new Date(v.sign_out_time).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' }) : <Badge colorScheme="green" fontSize="8px">On site</Badge>}</Td>
                        <Td fontSize="11px">{fmtDur(v.sign_in_time, v.sign_out_time) || '—'}</Td>
                        <Td>{!v.sign_out_time && <Button size="xs" variant="ghost" colorScheme="orange" onClick={() => adminSignOut(v.id)}>Out</Button>}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>

            {/* HISTORY */}
            <TabPanel px={0}>
              <HStack mb={3} flexWrap="wrap" gap={2}>
                <Input size="xs" type="date" value={historyFrom} onChange={e => setHistoryFrom(e.target.value)} w="130px" />
                <Text fontSize="11px" color="gray.500">to</Text>
                <Input size="xs" type="date" value={historyTo} onChange={e => setHistoryTo(e.target.value)} w="130px" />
                <Input size="xs" placeholder="Search name/company..." value={historySearch} onChange={e => setHistorySearch(e.target.value)} w="200px" />
                <Button size="xs" colorScheme="orange" onClick={fetchHistory}>Search</Button>
              </HStack>
              <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                <Table size="sm">
                  <Thead bg="gray.50"><Tr>
                    <Th fontSize="9px">Visitor</Th><Th fontSize="9px">Company</Th><Th fontSize="9px">Host</Th>
                    <Th fontSize="9px">Date</Th><Th fontSize="9px">In</Th><Th fontSize="9px">Out</Th><Th fontSize="9px">Duration</Th>
                  </Tr></Thead>
                  <Tbody>
                    {historyVisitors.length === 0 && <Tr><Td colSpan={7} textAlign="center" py={6} color="gray.400">No results — run a search above</Td></Tr>}
                    {historyVisitors.map(v => (
                      <Tr key={v.id}>
                        <Td fontSize="12px" fontWeight="500">{v.visitor_name}</Td>
                        <Td fontSize="11px">{v.visitor_company || '—'}</Td>
                        <Td fontSize="11px">{v.host_name}</Td>
                        <Td fontSize="11px">{new Date(v.sign_in_time).toLocaleDateString('en-GB')}</Td>
                        <Td fontSize="11px">{new Date(v.sign_in_time).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })}</Td>
                        <Td fontSize="11px">{v.sign_out_time ? new Date(v.sign_out_time).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' }) : '—'}</Td>
                        <Td fontSize="11px">{fmtDur(v.sign_in_time, v.sign_out_time) || '—'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>

            {/* PLANNED VISITS */}
            <TabPanel px={0}>
              <Flex justify="flex-end" mb={3}>
                <Button size="sm" leftIcon={<FiPlus />} colorScheme="orange" onClick={() => { setEditVisit(null); plannedModal.onOpen(); }}>New Planned Visit</Button>
              </Flex>
              <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                <Table size="sm">
                  <Thead bg="gray.50"><Tr>
                    <Th fontSize="9px">Title</Th><Th fontSize="9px">Project</Th><Th fontSize="9px">Host</Th>
                    <Th fontSize="9px">Expected</Th><Th fontSize="9px">Premises</Th><Th fontSize="9px">Status</Th><Th />
                  </Tr></Thead>
                  <Tbody>
                    {plannedVisits.length === 0 && <Tr><Td colSpan={7} textAlign="center" py={6} color="gray.400">No planned visits</Td></Tr>}
                    {plannedVisits.map(v => (
                      <Tr key={v.id}>
                        <Td><Text fontSize="12px" fontWeight="600">{v.title}</Text>{v.company_name && <Text fontSize="10px" color="gray.400">{v.company_name}</Text>}</Td>
                        <Td><Text fontSize="11px">{v.project_name}</Text>{v.task_title && <Text fontSize="10px" color="gray.400">{v.task_title}</Text>}</Td>
                        <Td fontSize="11px">{v.host_name}</Td>
                        <Td fontSize="11px">{fmtDt(v.expected_at)}</Td>
                        <Td fontSize="11px" color="gray.500">{v.premises_name || '—'}</Td>
                        <Td><Badge colorScheme={STATUS_COLORS[v.status]} fontSize="8px" textTransform="capitalize">{(v.status||'').replace('_',' ')}</Badge></Td>
                        <Td><HStack spacing={0}>
                          <IconButton icon={<FiEdit2 size={11}/>} size="xs" variant="ghost" onClick={() => { setEditVisit(v); plannedModal.onOpen(); }} aria-label="Edit" />
                          <IconButton icon={<FiTrash2 size={11}/>} size="xs" variant="ghost" colorScheme="red" onClick={() => deleteAV(v.id)} aria-label="Delete" />
                        </HStack></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>

            {/* PREMISES CONFIG */}
            <TabPanel px={0}>
              <Flex justify="flex-end" mb={3}>
                <Button size="sm" leftIcon={<FiPlus />} colorScheme="orange" onClick={() => { setEditPremises(null); premisesModal.onOpen(); }}>Add Premises</Button>
              </Flex>
              <VStack spacing={3} align="stretch">
                {premises.length === 0 && <Box textAlign="center" py={8} color="gray.400">No premises configured yet</Box>}
                {premises.map(p => (
                  <Box key={p.id} p={4} border="1px" borderColor="gray.200" borderRadius="lg">
                    <Flex justify="space-between" align="flex-start">
                      <HStack spacing={3} align="flex-start">
                        {p.logo_url && <Image src={`/api/files/${p.logo_url}`} h="36px" objectFit="contain" />}
                        <Box>
                          <Text fontWeight="700">{p.name}</Text>
                          <Text fontSize="12px" color="gray.500">{p.company_name}</Text>
                          {p.address && <Text fontSize="11px" color="gray.400">{p.address}</Text>}
                          <Badge colorScheme={p.enforcement_mode === 'strict' ? 'orange' : 'green'} fontSize="9px" mt={1}>
                            {p.enforcement_mode === 'strict' ? 'Enforced' : 'Open'}
                          </Badge>
                        </Box>
                      </HStack>
                      <HStack>
                        <Tooltip label="Copy kiosk URL">
                          <IconButton icon={<FiCopy size={13}/>} size="xs" variant="ghost" onClick={() => copyKioskUrl(p)} aria-label="Copy URL" />
                        </Tooltip>
                        <Tooltip label="Open kiosk">
                          <IconButton icon={<FiExternalLink size={13}/>} size="xs" variant="ghost" onClick={() => window.open(`/visitor/default/${p.slug}`, '_blank')} aria-label="Open kiosk" />
                        </Tooltip>
                        <IconButton icon={<FiEdit2 size={13}/>} size="xs" variant="ghost" onClick={() => { setEditPremises(p); premisesModal.onOpen(); }} aria-label="Edit" />
                      </HStack>
                    </Flex>
                    <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.100">
                      <Text fontSize="10px" color="gray.400" mb={1}>Kiosk URL:</Text>
                      <Code fontSize="10px" colorScheme="gray">{window.location.origin}/visitor/default/{p.slug}</Code>
                    </Box>
                  </Box>
                ))}
              </VStack>
            </TabPanel>

          </TabPanels>
        </Tabs>
      )}

      <PremisesModal isOpen={premisesModal.isOpen} onClose={premisesModal.onClose} onSaved={fetchAll} editPremises={editPremises} companies={companies} />
      <PlannedVisitModal isOpen={plannedModal.isOpen} onClose={plannedModal.onClose} onSaved={fetchAll} premisesList={premises} allUsers={allUsers} editVisit={editVisit} />
    </Box>
  );
}

export { PlannedVisitModal };
